import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { User } from '../../domain/model';
import { TokenService } from '../../../../shared/service/token.service';
import { LoginUseCase } from '../use-case/login.use-case';
import { RefreshTokenUseCase } from '../use-case/refresh-token.use-case';
import { ForgotPasswordUseCase } from '../use-case/forgot-password.use-case';
import { ChangePasswordUseCase } from '../use-case/change-password.use-case';
import { LogoutUseCase } from '../use-case/logout.use-case';
import {
  AuthChangePasswordRequest,
  AuthChangePasswordResponse,
  AuthForgotPasswordRequest,
  AuthForgotPasswordResponse,
  AuthLoginRequest,
  AuthLoginResponse,
  AuthLogoutResponse,
  AuthRefreshResponse,
} from '../../domain/dto';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private tokenService = inject(TokenService);
  private loginUseCase = inject(LoginUseCase);
  private refreshTokenUseCase = inject(RefreshTokenUseCase);
  private forgotPasswordUseCase = inject(ForgotPasswordUseCase);
  private changePasswordUseCase = inject(ChangePasswordUseCase);
  private logoutUseCase = inject(LogoutUseCase);

  private userSignal = signal<User | null>(null);

  readonly user = this.userSignal.asReadonly();
  readonly isAuthenticated = computed(() => this.userSignal() !== null);
  readonly userName = computed(() => this.userSignal()?.name || 'User');
  readonly userEmail = computed(() => this.userSignal()?.email || '');
  readonly userRole = computed(() => this.userSignal()?.role || '');
  readonly userInitials = computed(() => {
    const name = this.userSignal()?.name;
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  });

  constructor() {
    this.loadUser();
  }

  private loadUser(): void {
    const user = this.tokenService.getUserData<User>();
    this.userSignal.set(user);
  }

  login(request: AuthLoginRequest): Observable<HttpResponse<AuthLoginResponse>> {
    // Ensure each login starts from a clean local session state.
    this.clearUser();

    return this.loginUseCase.execute(request).pipe(
      tap((response) => {
        const loginData = response.body;
        if (loginData?.profile) {
          this.setCurrentUser(loginData?.profile);
        } else {
          // Never reuse a previous session profile when login payload is incomplete.
          this.clearUser();
        }
      }),
    );
  }

  forgotPassword(
    request: AuthForgotPasswordRequest,
  ): Observable<HttpResponse<AuthForgotPasswordResponse>> {
    return this.forgotPasswordUseCase.execute(request);
  }

  changePassword(
    recoveryToken: string,
    request: Omit<AuthChangePasswordRequest, 'recoveryToken'>,
  ): Observable<HttpResponse<AuthChangePasswordResponse>> {
    return this.changePasswordUseCase.execute({ recoveryToken, ...request });
  }

  logout(): Observable<HttpResponse<AuthLogoutResponse>> {
    return this.logoutUseCase.execute().pipe(
      tap({
        next: () => this.clearUser(),
        error: () => this.clearUser(),
      }),
    );
  }

  refreshToken(): Observable<HttpResponse<AuthRefreshResponse>> {
    return this.refreshTokenUseCase.execute().pipe(
      tap(() => {
        // Only cookies are returned and managed by the browser.
        // We just ensure the user profile is still loaded.
        if (!this.userSignal()) {
          this.loadUser();
        }
      }),
    );
  }

  getCurrentUser(): User | null {
    return this.userSignal();
  }

  setCurrentUser(profile: any): void {
    const user = {
      name: profile.name,
      email: (profile as any).email ?? '',
      role: profile.role,
      menu: profile.menu ?? [],
    };
    this.tokenService.setUserData(user);
    this.userSignal.set(user);
  }

  clearCurrentUser(): void {
    this.tokenService.clearTokens();
    this.userSignal.set(null);
  }

  setUser(user: User): void {
    this.tokenService.setUserData(user);
    this.userSignal.set(user);
  }

  updateUser(userData: Partial<User>): void {
    const currentUser = this.userSignal();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      this.tokenService.setUserData(updatedUser);
      this.userSignal.set(updatedUser);
    }
  }

  clearUser(): void {
    this.tokenService.clearTokens();
    this.userSignal.set(null);
  }

  clearUserOnlyIfCurrentSession(): boolean {
    const cleared = this.tokenService.clearTokensOnlyIfCurrentSession();
    this.userSignal.set(null);
    return cleared;
  }

  refreshUser(): void {
    this.loadUser();
  }
}


